import { useMemo } from "react";
import {
  ComposedChart, Area, Line, XAxis, YAxis,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import {
  BUDGET_CATEGORIES, SPENDING_DATA,
  MONTHLY_SPENDING_TREND, PROJECTED_MONTHS, fmt,
} from "../../data/mockData";
import { useIsMobile } from "../../hooks/useIsMobile";

// ─── Map budget category names → MONTHLY_SPENDING_TREND keys ────────────────
const CAT_KEY_MAP = {
  Housing:       null,             // fixed recurring cost
  Groceries:     "groceries",
  Transport:     "transport",
  Dining:        "dining",
  Subscriptions: "subscriptions",
  Shopping:      "shopping",
};

// Compute projected end-of-month spend per category
// Uses trailing 3-month average from MONTHLY_SPENDING_TREND
// Housing uses fixed rent amount (from PROJECTED_MONTHS utilities proxy)
function getProjected(catName) {
  if (catName === "Housing") return 1850; // fixed rent
  const key = CAT_KEY_MAP[catName];
  if (!key) return null;
  const last3 = MONTHLY_SPENDING_TREND.slice(-3);
  return Math.round(last3.reduce((s, m) => s + (m[key] || 0), 0) / 3);
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    "On Track":   { color: "#52D68A", bg: "#52D68A18", icon: "✓" },
    "Watch":      { color: "#E8C87A", bg: "#E8C87A18", icon: "◎" },
    "Over Budget":{ color: "#F4B8B8", bg: "#F4B8B818", icon: "⚠" },
  };
  const s = map[status] || map["On Track"];
  return (
    <span style={{
      fontSize: "9px", fontFamily: "'JetBrains Mono', monospace",
      color: s.color, background: s.bg,
      borderRadius: "5px", padding: "2px 7px",
      letterSpacing: "0.06em", fontWeight: "500",
    }}>{s.icon} {status}</span>
  );
}

// ─── Enhanced budget bar with projected tick ──────────────────────────────────
function ProjectedBudgetBar({ cat }) {
  const projected = getProjected(cat.name);
  const spentPct  = Math.min((cat.spent / cat.allocated) * 100, 100);
  const projPct   = projected != null ? Math.min((projected / cat.allocated) * 100, 120) : null;
  const over      = cat.spent > cat.allocated;
  const projOver  = projected != null && projected > cat.allocated;
  const projNear  = projected != null && projected > cat.allocated * 0.85 && !projOver;

  const status = projOver ? "Over Budget" : projNear ? "Watch" : "On Track";
  const projDelta = projected != null ? projected - cat.allocated : null;

  return (
    <div style={{ marginBottom: "22px" }}>
      {/* Top row: name + spent / budget */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "5px" }}>
        <span style={{ fontSize: "13px", color: "var(--text-secondary)", fontWeight: "500" }}>{cat.name}</span>
        <span style={{ fontSize: "12px", fontFamily: "'JetBrains Mono', monospace", color: over ? "#F87171" : "#888" }}>
          {fmt(cat.spent)} <span style={{ color: "var(--text-faint)" }}>/ {fmt(cat.allocated)}</span>
        </span>
      </div>

      {/* Bar track */}
      <div style={{ position: "relative", height: "8px", background: "var(--bg-hover)", borderRadius: "4px", marginBottom: "5px" }}>
        {/* Spent fill */}
        <div style={{
          position: "absolute", height: "100%",
          width: `${spentPct}%`,
          background: over ? "#F87171" : cat.color,
          borderRadius: "4px",
          transition: "width 0.8s cubic-bezier(0.4,0,0.2,1)",
        }} />

        {/* Projected end-of-month marker */}
        {projPct != null && projPct > spentPct && projPct <= 100 && (
          <div style={{
            position: "absolute",
            left: `${projPct}%`, top: "-3px", bottom: "-3px",
            width: "2px", borderRadius: "2px",
            background: projOver ? "#F4B8B8" : projNear ? "#E8C87A" : "#52D68A",
            opacity: 0.85,
            transform: "translateX(-50%)",
          }} />
        )}

        {/* Over-budget projected: glow on right edge */}
        {projPct != null && projPct > 100 && (
          <div style={{
            position: "absolute", right: "-4px", top: "-3px", bottom: "-3px",
            width: "4px", borderRadius: "2px",
            background: "#F4B8B8", boxShadow: "0 0 6px #F4B8B8",
          }} />
        )}

        {/* Projected label above marker (only if within bar) */}
        {projPct != null && projPct > spentPct && projPct <= 100 && (
          <div style={{
            position: "absolute",
            left: `${projPct}%`,
            top: "-18px",
            fontSize: "9px",
            fontFamily: "'JetBrains Mono', monospace",
            color: projOver ? "#F4B8B8" : projNear ? "#E8C87A" : "#52D68A",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}>
            ▾ {fmt(projected)}
          </div>
        )}
      </div>

      {/* Bottom row: % used + status */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
          {spentPct.toFixed(0)}% used
          {projected != null && (
            <span style={{ marginLeft: "6px", color: "var(--text-faint)" }}>
              · proj {projOver
                ? <span style={{ color: "#F4B8B8" }}>{fmt(projected)} (+{fmt(projDelta)})</span>
                : <span style={{ color: projNear ? "#E8C87A" : "#555" }}>{fmt(projected)}</span>
              }
            </span>
          )}
        </span>
        <StatusBadge status={status} />
      </div>
    </div>
  );
}

// ─── Custom tooltip for the chart ─────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-strong)",
      borderRadius: "10px", padding: "12px 16px", minWidth: "140px",
    }}>
      <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "6px" }}>
        {label}{payload[0]?.payload?.isProjected ? " (projected)" : ""}
      </div>
      {payload.map((p, i) => p.value != null && (
        <div key={i} style={{ fontSize: "12px", color: p.color || "#bbb", fontFamily: "'JetBrains Mono', monospace", marginBottom: "2px" }}>
          {p.name}: ${p.value.toLocaleString()}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function BudgetPage() {
  const isMobile = useIsMobile();
  const totalAllocated = BUDGET_CATEGORIES.reduce((s, c) => s + c.allocated, 0);
  const totalSpent     = BUDGET_CATEGORIES.reduce((s, c) => s + c.spent,     0);
  const totalRemaining = totalAllocated - totalSpent;

  // Projected total end-of-month
  const projectedTotal = useMemo(() =>
    BUDGET_CATEGORIES.reduce((s, c) => {
      const p = getProjected(c.name);
      return s + (p ?? c.spent);
    }, 0),
  []);

  // Build combined chart: 7 historical months + 2 projected
  const chartData = useMemo(() => [
    ...SPENDING_DATA.map(d => ({
      month: d.month, actual: d.spending, projected: null, isProjected: false,
    })),
    { month: "Apr '26", actual: null, projected: PROJECTED_MONTHS[1].base, isProjected: true },
    { month: "May '26", actual: null, projected: PROJECTED_MONTHS[2].base, isProjected: true },
  ], []);

  // Average of historical months for reference line
  const avgSpending = Math.round(
    SPENDING_DATA.reduce((s, d) => s + d.spending, 0) / SPENDING_DATA.length
  );

  // Categories "at risk" for the summary
  const atRisk = BUDGET_CATEGORIES.filter(c => {
    const p = getProjected(c.name);
    return p != null && p > c.allocated * 0.85;
  });

  return (
    <div>
      {/* ── Summary Cards ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "14px", marginBottom: "22px" }}>
        {[
          { label: "Total Budget",     value: fmt(totalAllocated), sub: "This month",               color: "var(--text-secondary)"     },
          { label: "Spent So Far",     value: fmt(totalSpent),     sub: `${((totalSpent/totalAllocated)*100).toFixed(0)}% of budget`, color: "#F87171"  },
          { label: "Projected EOM",    value: fmt(projectedTotal), sub: projectedTotal > totalAllocated ? `⚠ ${fmt(projectedTotal - totalAllocated)} over` : `${fmt(totalAllocated - projectedTotal)} under`, color: projectedTotal > totalAllocated ? "#F4B8B8" : "#52D68A" },
          { label: "Remaining Budget", value: fmt(totalRemaining), sub: `${atRisk.length} categor${atRisk.length === 1 ? "y" : "ies"} at risk`, color: totalRemaining >= 0 ? "#52D68A" : "#F4B8B8" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "14px", padding: "18px 20px" }}>
            <div style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>{s.label}</div>
            <div style={{ fontSize: "22px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: s.color }}>{s.value}</div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "4px" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "20px" }}>

        {/* ── Left: Enhanced Budget Bars ────────────────────────── */}
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "22px" }}>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Monthly Budget
            </div>
            <div style={{ display: "flex", gap: "12px", fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", alignItems: "center" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <div style={{ width: "12px", height: "4px", borderRadius: "2px", background: "#D4AF37" }} /> Spent
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <div style={{ width: "2px", height: "12px", borderRadius: "2px", background: "#52D68A" }} /> Proj. EOM
              </span>
            </div>
          </div>
          {BUDGET_CATEGORIES.map(cat => (
            <ProjectedBudgetBar key={cat.name} cat={cat} />
          ))}
        </div>

        {/* ── Right: Chart + Legend ─────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

          {/* Spending breakdown chart */}
          <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "24px", flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  Spending Breakdown
                </div>
                <div style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", marginTop: "3px" }}>
                  7-month history + 2-month projection
                </div>
              </div>
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <div style={{ width: "10px", height: "4px", borderRadius: "2px", background: "#F87171" }} />
                  <span style={{ fontSize: "9px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>Actual</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <div style={{ width: "16px", height: "0", borderTop: "2px dashed #E8C87A" }} />
                  <span style={{ fontSize: "9px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>Projected</span>
                </div>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="spGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#F87171" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F87171" stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="month"
                  tick={{ fill: "var(--text-faint)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }}
                  axisLine={false} tickLine={false}
                />
                <YAxis
                  tick={{ fill: "var(--text-faint)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }}
                  axisLine={false} tickLine={false}
                  tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                  width={34}
                />
                <Tooltip content={<ChartTooltip />} />

                {/* Average reference line */}
                <ReferenceLine
                  y={avgSpending}
                  stroke="var(--border-active)" strokeDasharray="3 3" strokeWidth={1}
                  label={{ value: "avg", position: "insideTopRight", fontSize: 9, fill: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}
                />

                {/* Divider between historical and projected */}
                <ReferenceLine
                  x="May"
                  stroke="var(--border)" strokeWidth={1}
                  label={{ value: "today →", position: "insideTopLeft", fontSize: 8, fill: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}
                />

                {/* Historical area */}
                <Area
                  type="monotone" dataKey="actual" name="Actual"
                  stroke="#F87171" strokeWidth={2}
                  fill="url(#spGrad)"
                  dot={false} activeDot={{ r: 4, fill: "#F87171" }}
                  connectNulls={false}
                />

                {/* Projected line */}
                <Line
                  type="monotone" dataKey="projected" name="Projected"
                  stroke="#E8C87A" strokeWidth={2}
                  strokeDasharray="6 3"
                  dot={{ r: 4, fill: "#E8C87A", strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: "#E8C87A" }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Category projection summary */}
          <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "20px" }}>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "14px" }}>
              End-of-Month Projection
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {BUDGET_CATEGORIES.map(cat => {
                const projected = getProjected(cat.name);
                if (projected == null) return null;
                const projOver  = projected > cat.allocated;
                const projNear  = projected > cat.allocated * 0.85 && !projOver;
                const fillPct   = Math.min((projected / cat.allocated) * 100, 100);
                const color     = projOver ? "#F4B8B8" : projNear ? "#E8C87A" : "#52D68A";
                return (
                  <div key={cat.name} style={{ background: "var(--bg-main)", borderRadius: "10px", padding: "12px 14px", border: `1px solid ${color}20` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "7px" }}>
                      <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{cat.name}</span>
                      <span style={{ fontSize: "11px", fontFamily: "'JetBrains Mono', monospace", color, fontWeight: "600" }}>{fmt(projected)}</span>
                    </div>
                    <div style={{ height: "4px", background: "var(--bg-hover)", borderRadius: "2px", marginBottom: "5px" }}>
                      <div style={{ height: "100%", width: `${fillPct}%`, background: color, borderRadius: "2px", opacity: 0.7 }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>of {fmt(cat.allocated)}</span>
                      <span style={{ fontSize: "10px", fontFamily: "'JetBrains Mono', monospace", color }}>
                        {projOver
                          ? `+${fmt(projected - cat.allocated)}`
                          : `${fmt(cat.allocated - projected)} left`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
